
import React, { PureComponent } from 'react';

import { MHTrackEvents } from '../../base/lib-meet-hour';
import { MEDIA_TYPE } from '../../base/media';
import { getLocalParticipant } from '../../base/participants';
import { E2EELabel } from '../../e2ee';
import { LocalRecordingLabel } from '../../local-recording';
import { NotificationsContainer } from '../../notifications/components';
import RecordingLabel from '../../recording/components/web/RecordingLabel';
import { TranscribingLabel } from '../../transcribing';
import { shouldDisplayTileView } from '../../video-layout';
import { VideoQualityLabel } from '../../video-quality';
import { shouldDisplayNotifications } from '../functions';

import InsecureRoomNameLabel from './web/InsecureRoomNameLabel';

// declare var interfaceConfig: Object;

// /* global APP, interfaceConfig */

// // XXX: We are not currently using state here, but in the future, when
// // interfaceConfig is part of redux we will. This will have to be retrieved from the store.
// const visibleButtons = new Set(interfaceConfig.TOOLBAR_BUTTONS);

/**
 * The type of the React {@code Component} props of {@link AbstractLabels}.
 */
export type AbstractProps = {

  /**
   * Set to {@code true} when the notifications are to be displayed.
   *
   * @protected
   * @type {boolean}
   */
  _notificationsVisible: boolean,

  /**
   * Conference room name.
   *
   * @protected
   * @type {string}
   */
  _room: string,

  /**
   * Whether or not the layout should change to support tile view mode.
   *
   * @protected
   * @type {boolean}
   */
  _shouldDisplayTileView: boolean,
};

/**
 * A container to hold video status labels, including recording status and
 * current large video quality.
 *
 * @augments Component
 */
export class AbstractConference<P: AbstractProps, S> extends PureComponent<P, S> {

    /**
     * Initializes a new {@code AbstractConference} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            level: -1
        };

        this._updateLevel = this._updateLevel.bind(this);
    }

    _updateLevel: (number) => void;

    /**
     * Updates the level of the meter.
     *
     * @param {number} num - The audio level provided by the mhTrack.
     * @returns {void}
     */
    _updateLevel(num) {
        this.setState({
            level: Math.floor(num / 0.125)
        });
    }

    /**
     * Subscribes to audio level chanages comming from the mhTrack.
     *
     * @returns {void}
     */
    _startListening() {
        const { mhTrack } = this.props;

        mhTrack && mhTrack.on(
            MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
            this._updateLevel);
    }

    /**
     * Unsubscribes from chanages comming from the mhTrack.
     *
     * @param {Object} mhTrack - The mhTrack to unsubscribe from.
     * @returns {void}
     */
    _stopListening(mhTrack) {
        mhTrack && mhTrack.off(MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, this._updateLevel);
        this.setState({
            level: -1
        });
    }

    /**
     * Implements React's {@link Component#componentDidUpdate}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (prevProps.mhTrack !== this.props.mhTrack) {
            this._stopListening(prevProps.mhTrack);
            this._startListening();
        }
    }


    /**
     * Implements React's {@link Component#componentDidMount}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._startListening();
    }

    /**
     * Implements React's {@link Component#componentWillUnmount}.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._stopListening(this.props.mhTrack);
    }

    /**
   * Renders the {@code LocalRecordingLabel}.
   *
   * @param {Object} props - The properties to be passed to
   * the {@code NotificationsContainer}.
   * @protected
   * @returns {React$Element}
   */
    renderNotificationsContainer(props: ?Object) {
        if (this.props._notificationsVisible) {
            return React.createElement(NotificationsContainer, props);
        }

        return null;
    }

    /**
   * Renders the {@code E2EELabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderE2EELabel() {
        return <E2EELabel />;
    }

    /**
   * Renders the {@code LocalRecordingLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderLocalRecordingLabel() {
        return <LocalRecordingLabel />;
    }

    /**
   * Renders the {@code RecordingLabel} that is platform independent.
   *
   * @param {string} mode - The recording mode that this label is rendered
   * for.
   * @protected
   * @returns {React$Element}
   */
    _renderRecordingLabel(mode: string) {
        return <RecordingLabel mode = { mode } />;
    }

    /**
   * Renders the {@code TranscribingLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderTranscribingLabel() {
        return <TranscribingLabel />;
    }

    /**
   * Renders the {@code InsecureRoomNameLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderInsecureRoomNameLabel() {
        return <InsecureRoomNameLabel />;
    }

    /**
   * Renders the {@code VideoQualityLabel} that is platform independent.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderVideoQualityLabel() {
        return <VideoQualityLabel />;
    }

    //     _shouldShowButton: (string) => boolean;

//     /**
//    * Returns if a button name has been explicitly configured to be displayed.
//    *
//    * @param {string} buttonName - The name of the button, as expected in
//    * {@link interfaceConfig}.
//    * @private
//    * @returns {boolean} True if the button should be displayed.
//    */
//     _shouldShowButton(buttonName) {
//         return this.props._visibleButtons.has(buttonName);
//     }
}

/**
 * Maps (parts of) the redux state to the associated props of the {@link Labels}
 * {@code Component}.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {AbstractProps}
 */
export function abstractMapStateToProps(state: Object) {
    const tracks = state['features/base/tracks'];
    const tracksNow = tracks.filter(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && t.videoType === 'desktop' && t.muted === false);
    const isSharing = tracksNow.length > 0;
    const participant = state['features/base/participants'].find(p => p.local);
    const preregData = state['features/base/mtApi']?.pregData;
    const showTranscribingLabel = state['features/transcribing'].isTranscribing;
    const isRecordingorLiveStreaming = state['features/recording'].recordingMode !== undefined;
    const { participantSharingVideo } = state['features/shared-video'];
    const localAudioTrack = tracks.find(t => t.local && t.mediaType === MEDIA_TYPE.AUDIO);

    // const screenSharingParticipants = getScreenshareParticipantIds(state);
    const localParticipant = getLocalParticipant(state);
    const moderatorPinnedParticipant = state['features/base/participants'].find(p => p.moderatorPin);
    const { getUrl } = state['features/donorbox'];
    const { clickAndPledgeUrl } = state['features/clickandpledge'];
    const { interfaceConfig } = state['features/base/config'];
    const {
        SHOW_MEET_HOUR_WATERMARK
    } = interfaceConfig;
    const screenSharingParticipantId = state['features/base/participants'].filter(p => p.isFakeParticipant === 'LocalScreenShare' || p.isFakeParticipant === 'RemoteScreenShare') ?? [];

    // const buttons = new Set(interfaceConfig.TOOLBAR_BUTTONS);

    return {
        _notificationsVisible: shouldDisplayNotifications(state),
        _room: state['features/base/conference'].room,
        _shouldDisplayTileView: shouldDisplayTileView(state),
        _isOpen: state['features/chat'].isOpen,
        nbUnreadMessages: state['features/chat']?.nbUnreadMessages,

        filmStripView: state['features/base/conference'].filmStripView,
        _isParticiapntsPaneOpen: state['features/participants-pane'].isOpen,
        _isNotesPaneOpen: Boolean(state['features/notes']?.isOpen),
        _isModerationPaneOpen: state['features/participants-pane'].isModerationOpen,
        _conferenceView: state['features/base/conference'].conferenceView,
        _fullScreen: state['features/toolbox'].fullScreen,
        _participantsLength: state['features/base/participants']?.length,
        _isScreenSharing: isSharing,
        _geturl: getUrl,
        SHOW_MEET_HOUR_WATERMARK,
        clickAndPledgeUrl,
        participantSharingVideo,
        mhTrack: localAudioTrack?.mhTrack,

        // _screenSharingParticipantId: screenShareParticipant[screenShareParticipant.length - 1]?.id,
        _localParticipant: localParticipant,
        moderatorPinnedParticipant,

        // _visibleButtons: equals(visibleButtons, buttons) ? visibleButtons : buttons,
        _screenSharingParticipantId: screenSharingParticipantId[screenSharingParticipantId.length - 1]?.id,
        _participant: participant,
        _isRecordingorLiveStreaming: state['features/recording'].recordingMode !== undefined,
        _isShowingLabels: showTranscribingLabel || isRecordingorLiveStreaming,
        largevideofullscreen: state['features/large-video'].largevideofullscreen,
        preregData
    };
}
